import { useAtomValue } from 'jotai';
import { Layer } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import {
  removedUnitIdsAtom,
  tcOverlappingUnitsAtom,
  tcSelectedDevAreaAtom,
  tcUnitNameColorMapAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import { typeCurveUnitsVisibilityAtom } from 'pages/type-curves/atoms';
import { unitShapeWorkingRingAtoms } from 'pages/type-curves/inventory-surface/map-atoms';
import { builderSurroundingsAtom } from 'pages/type-curves/inventory-surface/atoms';
import { buildUnitColorExpr } from 'pages/type-curves/tc-development-areas-layer';

/** The vector source `TcDevelopmentAreasLayer` owns. These layers ride on it. */
const SOURCE_ID = 'tc-development-areas';
const SOURCE_LAYER = 'development-areas';

/** Matches nothing — for a layer that must exist but draw nothing. */
const NONE_FILTER: any[] = ['==', 'name', ''];

/**
 * The Unit Builder's development-area layers.
 *
 * A DIFFERENT COMPOSITION of the same tiles that `TcDevelopmentAreasLayer`
 * draws on the page map, mounted in its place on the builder surface (that
 * component skips its own layers there). Splitting the layer set — rather than
 * teaching the page map's one to behave differently — keeps the page map exactly
 * as it is, and lets the builder answer to the builder's rules:
 *
 * - **Nothing paints the unit being edited.** `TcUnitShapeEditor` already draws
 *   it live from the working ring (`#0f62fe` at 0.3 under a `#0043ce` outline),
 *   so the tile's `selected` fill is dropped outright and the unit is filtered
 *   out of every remaining layer. Drawing it from the tile as well stacked a
 *   teal base wash and a second blue fill under the editor's own — the extra
 *   background — and left the unit at the mercy of the "Type Curve Units"
 *   checkbox, which could hide the very thing being edited.
 * - **No setback bands.** The tile emits one per unit; the builder wants only
 *   the scoped unit's, which `TcSetbackRingLayer` draws live from the spacing
 *   inputs.
 * - **Each toggle owns its own layers, completely.** "Type Curve Units" governs
 *   every layer here; Surroundings governs the surrounding units on top of that.
 *   Neither reaches the edited unit, because the tile never draws it.
 *
 * Same layer ids as the page map's, deliberately: `TcDevelopmentAreasLayer`
 * keeps the dev-area layers below the wells (`moveLayer` on `sourcedata`),
 * pulses the overlap fill and hit-tests clicks against these ids, and all of
 * that should keep working here. Ids are per map instance, so the two surfaces
 * do not collide.
 *
 * @return {JSX.Element} The builder's layer set.
 */
export const UnitBuilderDevAreaLayers = () => {
  const unitsVisible = useAtomValue(typeCurveUnitsVisibilityAtom);
  const surroundings = useAtomValue(builderSurroundingsAtom);
  const removedUnitIds = useAtomValue(removedUnitIdsAtom);
  const overlappingUnits = useAtomValue(tcOverlappingUnitsAtom);
  const unitNameColorMap = useAtomValue(tcUnitNameColorMapAtom);
  const selected = useAtomValue(tcSelectedDevAreaAtom);
  const workingRing = useAtomValue(unitShapeWorkingRingAtoms.builder);

  const layoutFor = (visible: boolean) => {
    return { visibility: visible ? ('visible' as const) : ('none' as const) };
  };
  // Hidden rather than unmounted so the source keeps its tiles and the page
  // map's ordering effect keeps finding these ids.
  const surroundingLayout = layoutFor(unitsVisible && surroundings);
  const overlapLayout = layoutFor(unitsVisible);

  // The unit the editor is drawing. Gated on `workingRing` so it is only
  // excluded once the editor actually has a shape on screen — before that the
  // tile is the only thing that could draw it.
  const editedName = workingRing
    ? (selected?.developmentAreaName ?? null)
    : null;

  const clauses: any[][] = [];
  const removedIds = [...removedUnitIds];
  // MVT shape features carry `development_area_id`; workspace-removed units are
  // excluded here exactly as the page map excludes them.
  if (removedIds.length > 0) {
    clauses.push(['!in', 'development_area_id', ...removedIds]);
  }
  if (editedName) clauses.push(['!=', 'name', editedName]);
  const withClauses = (filter: any[]): any[] => {
    return clauses.length > 0 ? ['all', filter, ...clauses] : filter;
  };

  const shapeFilter = withClauses(['==', 'type', 'shape']);

  const overlappingNames = overlappingUnits.map((unit) => unit.name);
  const overlapFilter =
    overlappingNames.length > 0
      ? withClauses([
          'all',
          ['in', 'name', ...overlappingNames],
          ['==', 'type', 'shape'],
        ])
      : NONE_FILTER;

  const coloredNames = Object.keys(unitNameColorMap);
  const coloredFilter =
    coloredNames.length > 0
      ? withClauses([
          'all',
          ['in', 'name', ...coloredNames],
          ['==', 'type', 'shape'],
        ])
      : NONE_FILTER;
  const coloredExpr = buildUnitColorExpr(unitNameColorMap, '#08bdba') as
    string | Expression;

  return (
    <>
      {/* Surrounding units: a light teal wash under a firmer outline, so they
          read as context rather than competing with the unit being edited. */}
      <Layer
        id="tc-dev-area-fill"
        source={SOURCE_ID}
        source-layer={SOURCE_LAYER}
        type="fill"
        layout={surroundingLayout}
        filter={shapeFilter}
        paint={{ 'fill-color': '#007d79', 'fill-opacity': 0.08 }}
      />
      <Layer
        id="tc-dev-area-line"
        source={SOURCE_ID}
        source-layer={SOURCE_LAYER}
        type="line"
        layout={surroundingLayout}
        filter={shapeFilter}
        paint={{
          'line-color': '#007d79',
          'line-width': 3,
          'line-opacity': 0.6,
        }}
      />
      {/* Colour-by rules, when any are applied to the map. */}
      <Layer
        id="tc-dev-area-colored-fill"
        source={SOURCE_ID}
        source-layer={SOURCE_LAYER}
        type="fill"
        layout={surroundingLayout}
        filter={coloredFilter}
        paint={{ 'fill-color': coloredExpr as any, 'fill-opacity': 0.35 }}
      />
      <Layer
        id="tc-dev-area-colored-line"
        source={SOURCE_ID}
        source-layer={SOURCE_LAYER}
        type="line"
        layout={surroundingLayout}
        filter={coloredFilter}
        paint={{ 'line-color': coloredExpr as any, 'line-width': 2 }}
      />
      {/* Units the workspace reports as overlapping. Not tied to Surroundings:
          an overlap is a conflict to resolve, not context to hide. */}
      <Layer
        id="tc-dev-area-overlap-fill"
        source={SOURCE_ID}
        source-layer={SOURCE_LAYER}
        type="fill"
        layout={overlapLayout}
        filter={overlapFilter}
        paint={{ 'fill-color': '#da1e28', 'fill-opacity': 1 }}
      />
      <Layer
        id="tc-dev-area-overlap-line"
        source={SOURCE_ID}
        source-layer={SOURCE_LAYER}
        type="line"
        layout={overlapLayout}
        filter={overlapFilter}
        paint={{ 'line-color': '#da1e28', 'line-width': 3 }}
      />
    </>
  );
};
